// src/App.jsx
import { useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { LoadScript } from "@react-google-maps/api";

// ✅ Keep ONLY the Chakra provider in index.jsx.
// import { ChakraProvider, defaultSystem } from "@chakra-ui/react";  // ❌ removed

import { Toaster } from "./components/ui/toaster"; // from CLI snippet
import { ColorModeProvider } from "./components/ui/color-mode"; // from CLI snippet

import { AuthProvider } from "./context/AuthContext";
import AuthContext from "./context/AuthContext";

// Layout
import Layout from "./components/layout/Layout";

// Pages
import HomePage from "./components/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import AnlageDetailPage from "./components/anlagen/AnlageDetailPage";
import PendingUsersPage from "./pages/PendingUsersPage";
import AnlagenList from "./components/anlagen/AnlagenList";
import PVForm from "./components/PVForm";
import PasswordChangePage from "./pages/PasswordChangePage";
import ProfilePage from "./pages/ProfilePage";
import NeueAnlagePage from "./pages/NeueAnlagePage";
import NeuerKundePage from "./pages/NeuerKundePage";
import Kundenliste from "./pages/Kundenliste";
import KundeViewPage from "./pages/KundeViewPage";
import KundeEditPage from "./pages/KundeEditPage";
import NeuerHerstellerPage from "./pages/NeuerHerstellerPage";
import Herstellerliste from "./pages/Herstellerliste";
import HerstellerEditPage from "./pages/HerstellerEditPage";
import HerstellerViewPage from "./pages/HerstellerViewPage";
import NeuerServicePartnerPage from "./pages/NeuerServicePartnerPage";
import ServicePartnerListe from "./pages/ServicePartnerListe";
import ServicePartnerViewPage from "./pages/ServicePartnerViewPage";
import ServicePartnerEditPage from "./pages/ServicePartnerEditPage";
import WartungsprotokollPage from "./pages/WartungsprotokollPage";
import ZusammenfassungProtokoll from "./pages/ZusammenfassungProtokoll";
import Wartungsliste from "./pages/wartung/Wartungsliste";
import Benutzerverwaltung from "./pages/admin/Benutzerverwaltung";
import AddUser from "./pages/admin/AddUser";
import EditUser from "./pages/admin/EditUser";

// Wrapper for protected routes
function PrivateRoute({ children }) {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  return (
    // ✅ Single provider setup: ChakraProvider is already in index.jsx with your `system`.
    <ColorModeProvider>
      <AuthProvider>
        <LoadScript googleMapsApiKey={googleMapsApiKey}>
          <AppRoutes />
          {/* ✅ Mount the Toaster once */}
          <Toaster />
        </LoadScript>
      </AuthProvider>
    </ColorModeProvider>
  );
}

function AppRoutes({ googleMapsApiKey }) {
  const { loadingAuth } = useContext(AuthContext);
  if (loadingAuth) return <div>Loading...</div>;
  // ...your Router and Routes here
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/anlagen"
          element={
            <PrivateRoute>
              <Layout>
                <AnlagenList />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/anlagen/neu"
          element={
            <PrivateRoute>
              <Layout>
                <NeueAnlagePage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/anlagen/:id"
          element={
            <PrivateRoute>
              <Layout>
                <AnlageDetailPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/anlagen/:id/edit"
          element={
            <PrivateRoute>
              <Layout>
                <PVForm />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/passwort"
          element={
            <PrivateRoute>
              <Layout>
                <PasswordChangePage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/pending"
          element={
            <PrivateRoute>
              <Layout>
                <PendingUsersPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/profil"
          element={
            <PrivateRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/neuer-kunde"
          element={
            <PrivateRoute>
              <Layout>
                <NeuerKundePage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/systemdaten/kundenliste"
          element={
            <PrivateRoute>
              <Layout>
                <Kundenliste />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/kunden/:id"
          element={
            <PrivateRoute>
              <Layout>
                <KundeViewPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/kunden/edit/:id"
          element={
            <PrivateRoute>
              <Layout>
                <KundeEditPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/neuer-hersteller"
          element={
            <PrivateRoute>
              <Layout>
                <NeuerHerstellerPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/systemdaten/herstellerliste"
          element={
            <PrivateRoute>
              <Layout>
                <Herstellerliste />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/hersteller/edit/:id"
          element={
            <PrivateRoute>
              <Layout>
                <HerstellerEditPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/systemdaten/hersteller/:id"
          element={
            <PrivateRoute>
              <Layout>
                <HerstellerViewPage />
              </Layout>
            </PrivateRoute>
          }
        />


        {/* --- SERVICE PARTNER --- */}

        <Route
          path="/service-partner/neu"
          element={
            <PrivateRoute>
              <Layout>
                <NeuerServicePartnerPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/service-partner/liste"
          element={
            <PrivateRoute>
              <Layout>
                <ServicePartnerListe />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/service-partner/:id"
          element={
            <PrivateRoute>
              <Layout>
                <ServicePartnerViewPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/service-partner/edit/:id"
          element={
            <PrivateRoute>
              <Layout>
                <ServicePartnerEditPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/wartung/wartungsprotokoll"
          element={
            <PrivateRoute>
              <Layout>
                <WartungsprotokollPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/wartung/zusammenfassung/:id"
          element={
            <PrivateRoute>
              <Layout>
                <ZusammenfassungProtokoll />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/wartung/liste"
          element={
            <PrivateRoute>
              <Layout>
                <Wartungsliste />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/wartung/protokoll/:id"
          element={
            <PrivateRoute>
              <Layout>
                <WartungsprotokollPage />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/benutzerverwaltung"
          element={
            <PrivateRoute>
              <Layout>
                <Benutzerverwaltung />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/benutzerverwaltung/add"
          element={
            <PrivateRoute>
              <Layout>
                <AddUser />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/benutzerverwaltung/edit/:id"
          element={
            <PrivateRoute>
              <Layout>
                <EditUser />
              </Layout>
            </PrivateRoute>
          }
        />

      </Routes>

    </Router>
  );
}